import * as React from "react";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ComponentModelDTO,
  ComponentType,
  useGroopitApiContext,
  useGroupContext,
} from "groopit-js";
import { FilterContext } from "../FeedFilter/State/FilterContext";
import { FilterDispatchContext } from "../FeedFilter/State/FilterDispatchContext";
import Highlighter from "react-highlight-words";
import { LinearProgress, Popover } from "@material-ui/core";
import { LoadScriptNext } from "@react-google-maps/api";
import { FlagPopover } from "../FlagPopover";
import { ExpandTags, NoteComponent } from "./NoteComponent";
import {
  AssignmentDTO,
  ComponentDataDTO,
  ComponentOperationType,
  GroopitFilterAssignmentDisplayDTO,
  GroopitFilterComponentDisplayDTO,
  GroopitFilterDisplayDTO,
} from "groopit-js/client";
import { ExpandableText } from "../Controls/ExpandableText";
import { FollowUpRequest } from "./FollowUpRequest";
import useLatestRef from "../react/react-use/useLatestRef";
import { HiddenIcon } from "../Icons/HiddenIcon";
import { FlagIcon } from "../Icons/FlagIcon";
import {
  getFilterKeywords,
  isComponentExcludedFromFilter,
  UnnamedGroopitFilterDisplayDTO,
} from "../FeedFilter/GroopitFilterDTO";
import { createFilterReplaceFilterAction } from "../FeedFilter/State/FilterActions";
import { GroopitFilterDisplay } from "../Models/GroopitFilterDisplay";

const ComponentMultipleChoice = (
  data: ComponentDataDTO,
  assignment: AssignmentDTO,
): JSX.Element => {
  const { group } = useGroupContext();
  const filterContext = useContext(FilterContext);
  const filterDispatchContext = useContext(FilterDispatchContext);
  const searchWords = useMemo(
    () => getFilterKeywords(filterContext?.filter),
    [filterContext?.filter],
  );
  const createTagFilter = useCallback(
    (o: string) => {
      const filter = new GroopitFilterDisplayDTO();
      filter.assignments = [
        new GroopitFilterAssignmentDisplayDTO({
          id: assignment.id,
          title: assignment.title ?? "",
          assignmentPhotoUrl: assignment.assignmentPhotoUrl,
          components: [
            new GroopitFilterComponentDisplayDTO({
              componentModelId: data.model.componentModel_Id,
              componentModelType: data.model.type,
              componentModelDescription: data.model.description ?? "",
              operation: ComponentOperationType.AllOf,
              value: [o],
            }),
          ],
        }),
      ];
      return new UnnamedGroopitFilterDisplayDTO({
        filter,
      });
    },
    [
      assignment.id,
      assignment.title,
      assignment.assignmentPhotoUrl,
      data.model.componentModel_Id,
      data.model.type,
      data.model.description,
    ],
  );

  return (
    <div className="text-display-normal text-color-secondary text-break-words tag-component">
      {data.options?.split("|").map((o, i) =>
        filterContext ? (
          <button
            type="button"
            key={i}
            className={`btn-groopit ${
              filterContext.filter.filter.tags.includes(o)
                ? "search-highlight"
                : ""
            }`}
            onClick={() =>
              filterDispatchContext?.filterDispatcher(
                createFilterReplaceFilterAction(createTagFilter(o)),
              )
            }
          >
            {searchWords.length > 0 ? (
              <Highlighter
                key={i}
                searchWords={searchWords}
                textToHighlight={`#${o}`}
                highlightClassName="search-highlight"
              ></Highlighter>
            ) : (
              <span>#{o}</span>
            )}
          </button>
        ) : (
          // This always links to us
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            key={i}
            className="btn-groopit"
            target={window.self === window.top ? "_self" : "_blank"}
            href={`/Groop/Groopits/${group.id}?filter=${encodeURIComponent(
              JSON.stringify(
                GroopitFilterDisplay.convertToGroopitFilter(
                  createTagFilter(o).filter,
                ),
              ),
            )}`}
          >
            <span>#{o}</span>
          </a>
        ),
      )}
    </div>
  );
};

const ComponentMultipleChoiceExtended = (
  data: ComponentDataDTO,
  assignment: AssignmentDTO,
): JSX.Element => {
  const { group } = useGroupContext();
  const filterContext = useContext(FilterContext);
  const filterDispatchContext = useContext(FilterDispatchContext);
  const searchWords = useMemo(
    () => getFilterKeywords(filterContext?.filter),
    [filterContext?.filter],
  );
  const createTagFilter = useCallback(
    (o: string) => {
      const filter = new GroopitFilterDisplayDTO();
      filter.assignments = [
        new GroopitFilterAssignmentDisplayDTO({
          id: assignment.id,
          title: assignment.title ?? "",
          assignmentPhotoUrl: assignment.assignmentPhotoUrl,
          components: [
            new GroopitFilterComponentDisplayDTO({
              componentModelId: data.model.componentModel_Id,
              componentModelType: data.model.type,
              componentModelDescription: data.model.description ?? "",
              operation: ComponentOperationType.AllOf,
              value: [o],
            }),
          ],
        }),
      ];
      return new UnnamedGroopitFilterDisplayDTO({
        filter,
      });
    },
    [
      assignment.id,
      assignment.title,
      assignment.assignmentPhotoUrl,
      data.model.componentModel_Id,
      data.model.type,
      data.model.description,
    ],
  );

  let parsedChoice: string;
  if (data.choice) {
    try {
      const jsonParsedChoice = JSON.parse(data.choice);

      if (typeof jsonParsedChoice === "number" && isFinite(jsonParsedChoice)) {
        parsedChoice = jsonParsedChoice.toString();
      } else {
        parsedChoice = jsonParsedChoice;
      }
    } catch {
      parsedChoice = data.choice;
    }
  } else {
    parsedChoice = "";
  }

  const parsedSelectedOptions: string[] = [];

  if (Array.isArray(parsedChoice)) {
    parsedChoice.forEach((choice: string) =>
      parsedSelectedOptions.push(choice),
    );
  } else {
    if (parsedChoice.length > 0) {
      parsedSelectedOptions.push(parsedChoice);
    }
  }

  return (
    <>
      {parsedSelectedOptions.map((option: string) =>
        filterContext ? (
          <button
            key={option}
            className={`text-display-normal btn-groopit text-break-words list-component ${
              filterContext &&
              filterContext.filter.filter.choices.includes(option)
                ? "search-highlight"
                : ""
            }`}
            onClick={() =>
              filterDispatchContext?.filterDispatcher(
                createFilterReplaceFilterAction(createTagFilter(option)),
              )
            }
          >
            <p className="whitespace-pre-wrap">
              {searchWords.length ? (
                <Highlighter
                  searchWords={getFilterKeywords(filterContext.filter)}
                  textToHighlight={option ?? ""}
                  highlightClassName="search-highlight"
                ></Highlighter>
              ) : (
                <span>{option}</span>
              )}
            </p>
          </button>
        ) : (
          // This always links to us
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            key={option}
            className="btn-groopit whitespace-pre-wrap"
            target={window.self === window.top ? "_self" : "_blank"}
            href={`/Groop/Groopits/${group.id}?filter=${encodeURIComponent(
              JSON.stringify(
                GroopitFilterDisplay.convertToGroopitFilter(
                  createTagFilter(option).filter,
                ),
              ),
            )}`}
          >
            <span>{option}</span>
          </a>
        ),
      )}
    </>
  );
};

const ComponentPicture = (component: ComponentDataDTO) => {
  const { pictureImageBase } = useGroupContext();

  return (
    <img
      title="picture"
      src={`${pictureImageBase}${component.image}`}
      className="picture-component"
    />
  );
};

const ComponentPictureSet = (component: ComponentDataDTO) => {
  const { pictureImageBase } = useGroupContext();

  return (
    <>
      {component.pictures
        ?.sort((a, b) => a.sequence - b.sequence)
        .map((p) => (
          <img
            title="picture"
            key={p.id}
            src={`${pictureImageBase}${p.image}`}
            className="picture-component"
          />
        ))}
    </>
  );
};
const ComponentFileSet = (component: ComponentDataDTO, groopit_id: string) => {
  return (
    <div className="fileset-component">
      {component.files
        ?.sort((a, b) => a.sequence - b.sequence)
        .map((p) => (
          <div key={p.fileId}>
            {/* The flex items are blowing the layout. I tried a bunch of ways of fixing
                but putting a single-column grid in was the only way I could get it to
                work consistently without having to do any sizing calculations */}
            <a
              className="grid grid-cols-1"
              download
              href={`/Groopit/File/${groopit_id}?fileId=${p.id}`}
            >
              <div className="flex items-center m-2">
                <img
                  title={`Download ${p.filename}`}
                  src="/Content/Images/download.png"
                  className="menu-icon"
                />
                <div className="filename">{p.filename}</div>
              </div>
            </a>
          </div>
        ))}
    </div>
  );
};

const LocationAddress = (props: google.maps.LatLngLiteral) => {
  const { lat, lng } = props;
  const geoCoder = useMemo(() => new google.maps.Geocoder(), []);
  const [address, setAddress] = useState<string>();
  useEffect(() => {
    const getAddress = async () => {
      geoCoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          setAddress(results[0].formatted_address);
        }
      });
    };
    getAddress();
  }, [geoCoder, lat, lng]);

  return address ? <div>{address}</div> : <LinearProgress />;
};

// Takes a lat/long pair and returns the text that represents it.
// Attempts to reverse-geocode the address using google geocoder

export const LocationText = (props: google.maps.LatLngLiteral): JSX.Element => {
  const { lat, lng } = props;

  return (
    <div>
      <LoadScriptNext
        googleMapsApiKey="AIzaSyC3-ueMlWqw1L4L1hVGabSfID7Z8Fg52Z8"
        libraries={["places"]}
      >
        <LocationAddress {...props} />
      </LoadScriptNext>
      <div>{`${lat},${lng}`}</div>
    </div>
  );
};

const ComponentLocation = (
  component: ComponentDataDTO,
  is_popover: boolean,
) => {
  const re = /\(([-\d.]+) ([-\d.]+)\)/;
  const [long, lat] = component.location?.geography?.wellKnownText
    ?.match(re)
    ?.slice(1) ?? ["0", "0"];
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  return (
    <div className="location-component">
      {is_popover ? (
        <LocationText lat={parseFloat(lat)} lng={parseFloat(long)} />
      ) : (
        <div className="relative">
          <img
            title="location"
            src={`https://maps.googleapis.com/maps/api/staticmap?size=640x360&zoom=13&markers=color:0xB44810|${lat},${long}&key=AIzaSyC3-ueMlWqw1L4L1hVGabSfID7Z8Fg52Z8`}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          />
          <Popover
            anchorEl={anchorEl}
            open={anchorEl !== null}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
          >
            <LocationText lat={parseFloat(lat)} lng={parseFloat(long)} />
          </Popover>
        </div>
      )}
    </div>
  );
};

const ComponentTemplate = (props: ComponentProps) => {
  const {
    component,
    assignment,
    model,
    allComponents,
    is_popover,
    groopit_id,
    openFollowUpEditor,
    dimmed,
  } = props;

  if (!component) {
    return null;
  }

  let componentToRender: JSX.Element;

  switch (model.type) {
    case ComponentType.Picture: {
      componentToRender = ComponentPicture(component);
      break;
    }
    case ComponentType.PictureSet: {
      componentToRender = ComponentPictureSet(component);
      break;
    }
    case ComponentType.Location: {
      componentToRender = ComponentLocation(component, is_popover);
      break;
    }
    case ComponentType.Note: {
      componentToRender = <NoteComponent {...component} />;
      break;
    }
    case ComponentType.MultipleChoice: {
      componentToRender = ComponentMultipleChoice(component, assignment);
      break;
    }
    case ComponentType.DateTime: {
      componentToRender = (
        <div
          className={`text-display-normal btn-groopit ${
            dimmed ? "btn-groopit-dimmed" : ""
          } date-component`}
        >
          {component.model.dateOnly
            ? component.timestamp?.toLocaleDateString()
            : component.timestamp?.toLocaleString()}
        </div>
      );
      break;
    }
    case ComponentType.MultipleChoiceExtended: {
      componentToRender = ComponentMultipleChoiceExtended(
        component,
        assignment,
      );
      break;
    }
    case ComponentType.FixedDecimal: {
      return (
        <div
          className={`text-display-normal text-color-secondary ${
            !model.parentModelComponentModel_Id ? "btn-groopit" : ""
          } ${!model.parentModelComponentModel_Id && dimmed ? "dimmed" : ""} ${
            model.parentModelComponentModel_Id && dimmed ? "dimmed" : ""
          } number-component`}
        >
          {component.formattedNumber}
        </div>
      );
    }
    case ComponentType.FileSet: {
      componentToRender = ComponentFileSet(component, groopit_id);
      break;
    }
    case ComponentType.SaveToSalesforce: {
      componentToRender = ComponentSaveToSalesforce(component, groopit_id);
      break;
    }
    case ComponentType.InheritedData: {
      componentToRender = ComponentInheritedData(component);
      break;
    }

    case ComponentType.FollowUpRequest: {
      componentToRender = FollowUpRequest({
        component: component,
        hasFollowUpFields: allComponents.some((c) => c.model.isFollowUpField),
        openEditor: openFollowUpEditor,
      });
      break;
    }
    default:
      componentToRender = <div>Unknown type {model.type}</div>;
      break;
  }

  return <div className={`${dimmed ? "dimmed" : ""}`}>{componentToRender}</div>;
};

const FollowUpComponentTemplate = (props: ComponentProps) => {
  const { component, model } = props;
  const { users } = useGroupContext();

  if (!component) {
    return null;
  }

  switch (model.type) {
    case ComponentType.Note: {
      return <span>{ExpandTags(component.notes, users)}</span>;
    }
    case ComponentType.MultipleChoice: {
      const data = component.options?.split("|") ?? [];
      return (
        <>
          {data.map((c, i) => (
            <Fragment key={c}>
              {i > 0 && <span> • </span>}
              <span>{c}</span>
            </Fragment>
          ))}
        </>
      );
    }
    case ComponentType.DateTime: {
      return (
        <span>
          {component.model.dateOnly
            ? component.timestamp?.toLocaleDateString()
            : component.timestamp?.toLocaleString()}
        </span>
      );
    }
    case ComponentType.MultipleChoiceExtended: {
      const data = JSON.parse(component.choice ?? "[]") as string[];
      return (
        <>
          {data.map((c, i) => (
            <Fragment key={c}>
              {i > 0 && <span> • </span>}
              <span>{c}</span>
            </Fragment>
          ))}
        </>
      );
    }
    case ComponentType.FixedDecimal: {
      return <span>{component.formattedNumber}</span>;
    }
    default:
      return <span>Unknown type {model.type}</span>;
  }
};

interface ComponentProps {
  component?: ComponentDataDTO;
  assignment: AssignmentDTO;
  model: ComponentModelDTO;
  allComponents: ComponentDataDTO[];
  dimmed?: boolean;
  is_popover: boolean;
  groopit_id: string;
  isLinkedToSalesforce: boolean;
  openFollowUpEditor: (anchor: HTMLElement) => void;
}

export const GroopitComponent = (props: ComponentProps) => {
  const { component, assignment, model, openFollowUpEditor } = { ...props };
  const filterContext = useContext(FilterContext);

  const [dimmed, setDimmed] = useState(props.dimmed ?? false);
  useEffect(() => {
    setDimmed(props.dimmed ?? false);
  }, [props.dimmed]);

  useEffect(() => {
    // If our parent told us our dimmed state, respect it
    if (props.dimmed !== undefined) {
      return;
    }

    setDimmed(
      isComponentExcludedFromFilter(
        filterContext?.filter.filter,
        assignment.id,
        model.componentModel_Id,
        model.parentModelComponentModel_Id,
      ),
    );
  }, [
    assignment.id,
    filterContext?.filter.filter,
    model.componentModel_Id,
    model.parentModelComponentModel_Id,
    props.dimmed,
  ]);

  if (!component && !model.isFlagField) {
    return null;
  }

  //
  // If there's a parent model, then we are rendering inside the
  // parent table component, so just render the component.
  //
  return model.parentModelComponentModel_Id ? (
    <>
      {model.type !== ComponentType.InheritedData && (
        <div className={`description ${dimmed ? "text-app-gray-06" : ""}`}>
          {model.description}
        </div>
      )}
      <ComponentTemplate {...props} dimmed={dimmed} />
    </>
  ) : model.isFollowUpField ? (
    <div
      className={`groopit-followup ${dimmed ? "dimmed" : ""}`}
      onClick={(e) => openFollowUpEditor(e.currentTarget)}
    >
      <span className="italic">{model.description}: </span>
      <FollowUpComponentTemplate {...props} dimmed={dimmed} />
    </div>
  ) : (
    <div className="groopit-component">
      {!model.isFlagField && (
        <div className={`${dimmed ? "dimmed" : ""}`}>
          <div
            className={`text-display-caption pt-4 pb-2 ${
              dimmed ? "text-app-gray-06" : "text-app-black"
            }`}
          >
            {model.type === ComponentType.FollowUpRequest
              ? "Follow-up"
              : model.description}
          </div>
        </div>
      )}
      {model.hiddenFromMembers && (
        <div>
          <HiddenIcon className="inline mr-2 w-5" />
          <span className="text-display-caption app-gray-03">
            Hidden from members, only visible to organizers
          </span>
        </div>
      )}
      {model.isFlagField ? (
        <div className={`${dimmed ? "dimmed" : ""}`}>
          <FlagComponent
            options={component?.options?.split("|").filter((e) => e) ?? []}
            {...props}
          />
        </div>
      ) : (
        <ComponentTemplate {...props} dimmed={dimmed} />
      )}
    </div>
  );
};

interface FlagComponentProps {
  model: ComponentModelDTO;
  options: string[];
  groopit_id: string;
  isLinkedToSalesforce: boolean;
}

export const FlagComponent = (props: FlagComponentProps) => {
  const { model, options, groopit_id } = { ...props };
  const choices = model.options?.split("|") ?? [];
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [selected, setSelected] = useState(
    options.sort((a, b) => choices.indexOf(a) - choices.indexOf(b)),
  );
  const api = useLatestRef(useGroopitApiContext());

  if (model.type !== ComponentType.MultipleChoice) {
    return <div>Unsupported type {model.type}</div>;
  }

  const handleSelection = async (newSel: string[]) => {
    const sorted = newSel.sort(
      (a, b) => choices.indexOf(a) - choices.indexOf(b),
    );
    // This really can't be null. It's not marked as required because it's
    // assigned by the database but anybody other than the creation UI
    // will always see this field. Using a negative value just in case
    // it ever _does_ pop up in a log for some unforseen reason
    await api.current.groopit.setFlagField(
      groopit_id,
      model.componentModel_Id ?? -1,
      sorted,
    );
    setSelected(sorted);
  };

  return (
    <div>
      {selected.length ? (
        <div
          className="cursor-pointer"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {selected.map((o, i) => (
            <div key={i} className="groopit-flag">
              {o}
            </div>
          ))}
        </div>
      ) : (
        <div
          className="text-display-subtitle app-gray-03 pt-4 pb-2 cursor-pointer"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <FlagIcon className="menu-icon" />
          {model.description}
        </div>
      )}
      <FlagPopover
        {...props}
        anchorEl={anchorEl}
        open={anchorEl != null}
        options={model.options?.split("|") ?? []}
        multiple={!!model.multiple}
        selected={selected}
        title={model.description}
        name={`${groopit_id}_${model.componentModel_Id}`}
        onSelectionChange={(sel) => handleSelection(sel)}
        onClose={() => {
          setAnchorEl(undefined);
        }}
      ></FlagPopover>
    </div>
  );
};

const ComponentSaveToSalesforce = (
  component: ComponentDataDTO,
  groopit_id: string,
) => {
  const { externalId, displayName } = component;
  const filterContext = useContext(FilterContext);
  const searchWords = useMemo(
    () => getFilterKeywords(filterContext?.filter),
    [filterContext?.filter],
  );

  return (
    <div className="text-display-normal text-color-secondary">
      <a
        className="btn-groopit"
        href={`/Groopit/Salesforce/${groopit_id}?objectId=${externalId}`}
      >
        {searchWords.length > 0 ? (
          <Highlighter
            searchWords={searchWords}
            textToHighlight={displayName ?? ""}
            highlightClassName="search-highlight"
          ></Highlighter>
        ) : (
          displayName
        )}
      </a>
    </div>
  );
};

const ComponentInheritedData = (component: ComponentDataDTO) => {
  const { model, displayValue } = component;
  const filterContext = useContext(FilterContext);
  const searchWords = useMemo(
    () => getFilterKeywords(filterContext?.filter),
    [filterContext?.filter],
  );

  return (
    <div className="text-display-caption">
      <ExpandableText collapsedSize={45}>
        <span>{model.externalFieldLabel}: </span>
        {searchWords.length > 0 ? (
          <Highlighter
            searchWords={searchWords}
            textToHighlight={displayValue ?? ""}
            highlightClassName="search-highlight"
          ></Highlighter>
        ) : (
          <span>{displayValue}</span>
        )}
      </ExpandableText>
    </div>
  );
};
